/**
 * Interview Progress — methodology-hidden coverage affordance.
 *
 * TRADE-SECRET CONTRACT (87-05 methodology-hidden design): this component is
 * driven ONLY by the founder-friendly covered-topic LABELS the server returns
 * (`coveredTopics` / `gapsCoveredLabels`). It NEVER references a dimension id,
 * sub-criterion id, weight, or numeric per-dimension breakdown — surfacing any
 * of those here would undo the methodology-hidden guarantee. Do not import any
 * scoring-methodology constant into this file.
 *
 * It reflects "how much of the conversation we've covered" in warm-mentor
 * language: a soft encouraging line plus friendly, wrapping topic chips.
 */

import React, { memo, useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated';
import { Check, ChevronDown, ChevronRight, Minus } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import type {
  DimensionCoverage,
  DimensionCoverageState,
} from '@/lib/interview/types';
import { coverageStateMeta, visibleDimensionCoverage } from './interview-coverage';

export interface InterviewProgressProps {
  /**
   * Founder-friendly covered-topic labels from the server (e.g. "Team",
   * "Market", "Traction"). NEVER dimension / sub-criterion ids.
   */
  coveredTopics: string[];
  /**
   * Per-dimension qualitative coverage (Task 2.1): dimension NAME + 3-state
   * marker ONLY. NEVER a number, score, weight, or sub-criterion count.
   */
  dimensionCoverage?: DimensionCoverage[];
  /** True once the founder has wrapped up the conversation. */
  isComplete?: boolean;
}

/** Warm, non-numeric progress copy — scales with how much has been covered. */
function encouragement(count: number, isComplete: boolean): string {
  if (isComplete) return "That's everything — thank you for walking me through it.";
  if (count === 0) return "Let's talk through your startup, one topic at a time.";
  if (count <= 2) return 'Great start — a few more areas to explore together.';
  if (count <= 4) return "We've covered a lot. Just a little more to go.";
  return "Almost there — you're giving me a really full picture.";
}

function InterviewProgressComponent({
  coveredTopics,
  dimensionCoverage,
  isComplete = false,
}: InterviewProgressProps) {
  const colors = useThemeColors();
  const [expanded, setExpanded] = useState(false);

  // Only ever render what the server explicitly hands us as covered labels —
  // never a fixed dimension list (count/order would leak methodology structure).
  const topics = coveredTopics.filter((t) => !!t && t.trim().length > 0);

  // Per-dimension coverage rows (3-state qualitative only). Filtered defensively
  // so a malformed/blank row never renders an empty chip.
  const coverageRows = visibleDimensionCoverage(dimensionCoverage);

  return (
    <Animated.View
      layout={LinearTransition.springify().damping(20)}
      className="px-5 pt-3 pb-3 border-b"
      style={{
        backgroundColor: colors.surface.primary,
        borderColor: colors.border.light,
      }}
    >
      {/* Encouraging, non-methodology progress line */}
      <Text
        numberOfLines={2}
        className="text-[13px] leading-5 font-medium"
        style={{ color: colors.text.secondary }}
      >
        {encouragement(topics.length, isComplete)}
      </Text>

      {/* Covered-topic chips — wrap so they never overflow on narrow screens */}
      {topics.length > 0 && (
        <View className="flex-row flex-wrap mt-2.5" style={{ gap: 8 }}>
          {topics.map((topic, i) => (
            <Animated.View
              key={`covered-${i}-${topic}`}
              entering={FadeIn.duration(260)}
              layout={LinearTransition.springify().damping(18)}
              className="flex-row items-center rounded-full px-3 py-1.5"
              style={{
                maxWidth: isWeb ? 240 : 180,
                backgroundColor: colors.primary[600],
              }}
            >
              <Check size={13} color={colors.text.inverse} strokeWidth={3} />
              <Text
                numberOfLines={1}
                className="text-xs font-semibold ml-1.5 flex-shrink"
                style={{ color: colors.text.inverse }}
              >
                {topic}
              </Text>
            </Animated.View>
          ))}
        </View>
      )}

      {/* Per-dimension coverage panel — expandable, 3-state QUALITATIVE only.
          Dimension names are public; no numbers/scores/weights ever render. */}
      {coverageRows.length > 0 && (
        <View className="mt-3">
          <Pressable
            onPress={() => setExpanded((v) => !v)}
            accessibilityRole="button"
            accessibilityLabel="Toggle coverage details"
            className="flex-row items-center"
            style={{ gap: 4 }}
            hitSlop={8}
          >
            {expanded ? (
              <ChevronDown size={14} color={colors.text.tertiary} strokeWidth={2.5} />
            ) : (
              <ChevronRight size={14} color={colors.text.tertiary} strokeWidth={2.5} />
            )}
            <Text
              numberOfLines={1}
              className="text-[12px] font-semibold flex-shrink"
              style={{ color: colors.text.tertiary }}
            >
              {expanded ? 'Hide what we’ve covered' : 'See what we’ve covered'}
            </Text>
          </Pressable>

          {expanded && (
            <Animated.View
              entering={FadeIn.duration(220)}
              className="flex-row flex-wrap mt-2.5"
              style={{ gap: 8 }}
            >
              {coverageRows.map((row, i) => (
                <CoverageChip
                  key={`dim-cov-${i}-${row.label}`}
                  label={row.label}
                  state={row.state}
                  colors={colors}
                />
              ))}
            </Animated.View>
          )}
        </View>
      )}
    </Animated.View>
  );
}

// =============================================================================
// Coverage chip — dimension name + 3-state qualitative marker (no numbers)
// =============================================================================

type Colors = ReturnType<typeof useThemeColors>;

/** Theme-aware background/foreground per qualitative tone. */
function chipTone(state: DimensionCoverageState, colors: Colors): {
  bg: string;
  fg: string;
} {
  switch (state) {
    case 'captured':
      return { bg: colors.traffic.greenLight, fg: colors.success };
    case 'light':
      return { bg: colors.amber.light, fg: colors.amber.main };
    case 'not_yet':
    default:
      return { bg: colors.surface.tertiary, fg: colors.text.tertiary };
  }
}

const CoverageChip = memo(function CoverageChip({
  label,
  state,
  colors,
}: {
  label: string;
  state: DimensionCoverageState;
  colors: Colors;
}) {
  const { bg, fg } = chipTone(state, colors);
  const { statusLabel } = coverageStateMeta(state);

  return (
    <View
      className="flex-row items-center rounded-full px-3 py-1.5"
      style={{ maxWidth: isWeb ? 260 : 200, backgroundColor: bg }}
    >
      {state === 'captured' ? (
        <Check size={12} color={fg} strokeWidth={3} />
      ) : (
        <Minus size={12} color={fg} strokeWidth={3} />
      )}
      <Text
        numberOfLines={1}
        className="text-[11px] font-semibold ml-1.5 flex-shrink"
        style={{ color: fg }}
      >
        {label}
      </Text>
      <Text
        numberOfLines={1}
        className="text-[11px] ml-1.5"
        style={{ color: fg, opacity: 0.75 }}
      >
        {'·'} {statusLabel}
      </Text>
    </View>
  );
});

export const InterviewProgress = memo(InterviewProgressComponent);
